import type { CreateView, Page, UpdateView, View } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/views');

export interface ViewListFilters {
  projectId?: string;
  /** Only the caller's own views. */
  mine?: boolean;
}

/** `GET /admin/v1/views?projectId=&mine=` */
export function useViews(filters: ViewListFilters = {}, pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<View>({
    queryKey: keys.views.list({ ...filters, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<View>>('GET', base, {
        query: {
          projectId: filters.projectId,
          mine: filters.mine === undefined ? undefined : String(filters.mine),
          ...pageQuery(cursor, pageSize),
        },
        signal,
      }),
  });
}

export function useView(viewId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.views.detail(viewId ?? ''),
    queryFn: ({ signal }) => request<View>('GET', `${base}/${viewId}`, { signal }),
    enabled: viewId !== undefined,
  });
}

export function useCreateView() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateView) => request<View>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.views.all),
  });
}

export function useUpdateView() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateView }) =>
      request<View>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (_view, { id }) => invalidate(keys.views.all, keys.views.run(id)),
  });
}

export function useDeleteView() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: (_void, id) => {
      remove(keys.views.detail(id), keys.views.run(id));
      return invalidate(keys.views.all);
    },
  });
}
